/**
 * Forgot Password Page
 *
 * The send is fire-and-forget by design: the backend answers 202 whether or
 * not the address has an account, and (DOP-275) whether or not it actually
 * mailed anything, so nothing here can tell the user "we sent it" with any
 * more confidence than "we tried". That is what shapes the confirmation --
 * it names the address, points at the spam folder, and offers a resend,
 * because those are the three things a user with no mail in their inbox can
 * actually act on.
 *
 * The confirmation is a modal over the card, not a state that replaces it:
 * the form still holds the address they typed, so `Use a different email` is
 * a correction rather than a retype.
 *
 * The send and its resend cooldown live in `useForgotPasswordSend`, shared
 * with the same flow on the Settings page.
 */

import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ForgotPasswordSentDialog from "../components/ForgotPasswordSentDialog";
import { useForgotPasswordSend } from "../hooks/useForgotPasswordSend";

export const ForgotPassword: React.FC = () => {
	const [email, setEmail] = useState("");
	const emailRef = useRef<HTMLInputElement | null>(null);
	const { sentTo, sending, error, cooldownSeconds, send, resend, reset } =
		useForgotPasswordSend();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		await send(email);
	};

	// Escape and `Use a different email` are the same act, so they run the same
	// code. Focus goes back to the field rather than to the button that opened
	// the dialog: the reason to close this is to edit the address.
	const handleCloseDialog = useCallback(() => {
		reset();
		emailRef.current?.focus();
	}, [reset]);

	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
			<div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
				<div>
					<h2 className="text-3xl font-bold text-gray-900">
						Forgot your password?
					</h2>
					<p className="mt-2 text-sm text-gray-600">
						Enter your email address and we'll send you instructions to reset
						your password.
					</p>
				</div>

				<form className="mt-8 space-y-6" onSubmit={handleSubmit}>
					{/* A failed FIRST send has no dialog to appear in; a failed resend
					    does, and is passed down to it instead. */}
					{error && sentTo === null && (
						<div className="rounded-md bg-red-50 border border-red-200 p-4">
							<p className="text-sm text-red-800">{error}</p>
						</div>
					)}

					<div>
						<label
							htmlFor="email"
							className="block text-sm font-medium text-gray-700"
						>
							Email Address<span className="text-red-500">*</span>
						</label>
						<input
							id="email"
							name="email"
							type="email"
							autoComplete="email"
							required
							ref={emailRef}
							value={email}
							onChange={(e) => setEmail(e.target.value)}
							className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-teal-500 focus:border-teal-500 sm:text-sm"
							placeholder="Enter your email address"
						/>
					</div>

					<div>
						<button
							type="submit"
							disabled={sending}
							className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-700 hover:bg-teal-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{sending ? "Sending..." : "Send reset instructions"}
						</button>
					</div>

					<div className="text-center">
						<Link
							to="/login"
							className="text-sm font-medium text-teal-600 hover:text-teal-500"
						>
							Back to login
						</Link>
					</div>
				</form>
			</div>

			{sentTo !== null && (
				<ForgotPasswordSentDialog
					email={sentTo}
					cooldownSeconds={cooldownSeconds}
					sending={sending}
					error={error}
					onResend={resend}
					onClose={handleCloseDialog}
				/>
			)}
		</div>
	);
};

export default ForgotPassword;
